import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    FaClock, FaCheckCircle, FaFileDownload, FaFileCsv,
    FaMapMarkerAlt, FaStopwatch, FaUtensils, FaEuroSign,
    FaChevronRight, FaChevronLeft, FaEdit, FaTimes, FaCalendarAlt, FaCalendarDay, FaListUl, FaCommentDots, FaTrash, FaExclamationTriangle
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { formatLocalDate } from '../../utils/timesheetHelpers';

const TabMyHours = ({
    staffTimesheets, getWeeklyStats, exportWeeklyToCSV,
    exportWeeklyToPDF, loadingStaff, fetchStaffData,
    activeWorkSession, elapsedTime, handleStartSession, handleStopSession,
    nearestProject, handleQuickValid7h, locationStatus, bonuses,
    todaySummary, pb, user, todayPlanning = [], staffProjects = [],
    setActiveTab
}) => {
    const [viewMode, setViewMode] = useState('week'); // 'week', 'month', 'day'
    const [currentDate, setCurrentDate] = useState(new Date());

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingEntry, setEditingEntry] = useState(null);
    
    const [isClaimModalOpen, setIsClaimModalOpen] = useState(false);
    const [claimEntry, setClaimEntry] = useState(null);
    const [claimReason, setClaimReason] = useState('');

    /*
    // --- GROUPING LOGIC (Preserved from original) ---
    const groupedTimesheets = useMemo(() => {
        const groups = {};
        staffTimesheets.forEach(ts => {
            if (ts.worker !== user.id) return;
            const dateKey = ts.date?.substring(0, 10);
            if (!dateKey) return;

            const tsHours = parseFloat(ts.hours) || 0;
            const statusPriority = { 'litige': 5, 'rejected': 4, 'pending_rh': 3, 'pending_manager': 2, 'approved': 1, '': 2, null: 2 };

            if (!groups[dateKey]) {
                groups[dateKey] = {
                    id: ts.id,
                    date: ts.date,
                    morning_start: ts.morning_start,
                    morning_end: ts.morning_end,
                    afternoon_start: ts.afternoon_start,
                    afternoon_end: ts.afternoon_end,
                    hours: tsHours,
                    status: ts.status || 'pending_manager',
                    projects: ts.expand?.chantier ? [ts.expand.chantier] : [],
                    originalEntries: [ts],
                    description: ts.description || ''
                };
            } else {
                const group = groups[dateKey];
                if (statusPriority[ts.status] > statusPriority[group.status]) {
                    group.status = ts.status || 'pending_manager';
                }
                if (ts.morning_start && (!group.morning_start || ts.morning_start < group.morning_start)) group.morning_start = ts.morning_start;
                if (ts.afternoon_end && (!group.afternoon_end || ts.afternoon_end > group.afternoon_end)) group.afternoon_end = ts.afternoon_end;
                if (ts.morning_end && ts.morning_end < "13:30") if (!group.morning_end || ts.morning_end > group.morning_end) group.morning_end = ts.morning_end;
                if (ts.afternoon_start && ts.afternoon_start >= "12:30") if (!group.afternoon_start || ts.afternoon_start < group.afternoon_start) group.afternoon_start = ts.afternoon_start;

                if (ts.expand?.chantier && !group.projects.some(p => p.id === ts.expand.chantier.id)) group.projects.push(ts.expand.chantier);
                group.originalEntries.push(ts);
            }
        });

        Object.values(groups).forEach(group => {
            const calc = (start, end) => {
                if (!start || !end || start === "--:--" || end === "--:--") return 0;
                const [hS, mS] = start.split(':').map(Number);
                const [hE, mE] = end.split(':').map(Number);
                return Math.max(0, (hE + mE / 60) - (hS + mS / 60));
            };
            const total = calc(group.morning_start, group.morning_end) + calc(group.afternoon_start, group.afternoon_end);
            if (total > 0) {
                group.hours = parseFloat(total.toFixed(2));
            }
        });

        const todayStr = formatLocalDate(new Date());
        todayPlanning.forEach(event => {
            const dateKey = event.start?.substring(0, 10) || formatLocalDate(event.fullDate);
            if (!dateKey || dateKey > todayStr) return;

            if (!groups[dateKey]) {
                groups[dateKey] = {
                    id: `suggested-${dateKey}`,
                    date: dateKey,
                    isSuggested: true,
                    morning_start: "08:00",
                    morning_end: "12:00",
                    afternoon_start: "13:00",
                    afternoon_end: "17:00",
                    hours: 8,
                    status: 'suggested',
                    projects: event.expand?.project ? [event.expand.project] : [],
                    description: `Prévu : ${event.task || event.title || 'Mission'}`
                };
            }
        });

        return Object.values(groups).sort((a, b) => b.date.localeCompare(a.date));
    }, [staffTimesheets, todayPlanning, user.id]);
    */

    const rawTimesheets = useMemo(() => {
        return staffTimesheets
            .filter(ts => ts.worker === user.id)
            .map(ts => ({
                ...ts,
                projects: ts.expand?.chantier ? [ts.expand.chantier] : [],
                originalEntries: [ts]
            }))
            .sort((a, b) => b.date.localeCompare(a.date));
    }, [staffTimesheets, user.id]);

    const groupedTimesheets = rawTimesheets;

    const formatElapsedTime = (seconds) => {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = seconds % 60;
        return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    };

    // Calendar Helpers
    const getWeekDays = (baseDate) => {
        const days = [];
        const current = new Date(baseDate);
        const dayOfWeek = current.getDay() === 0 ? 6 : current.getDay() - 1; // Mon=0, Sun=6
        current.setDate(current.getDate() - dayOfWeek); // Start of week

        for (let i = 0; i < 7; i++) {
            const d = new Date(current);
            d.setDate(d.getDate() + i);
            days.push(d);
        }
        return days;
    };

    const getMonthDays = (year, month) => {
        const firstDay = new Date(year, month, 1).getDay();
        const startOffset = firstDay === 0 ? 6 : firstDay - 1;
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        
        const days = Array(startOffset).fill(null);
        for (let i = 1; i <= daysInMonth; i++) {
            days.push(new Date(year, month, i));
        }
        return days;
    };

    const getStatusForDate = (dateObj) => {
        if (!dateObj) return null;
        const dateStr = formatLocalDate(dateObj);
        const todayStr = formatLocalDate(new Date());
        if (dateStr > todayStr) return 'future';

        const entry = groupedTimesheets.find(ts => {
            const tsDate = ts.date ? ts.date.split('T')[0] : ts.date;
            return tsDate === dateStr;
        });
        if (entry) return entry.status;
        return 'missing';
    };

    const weekDays = getWeekDays(currentDate);
    const monthDays = getMonthDays(currentDate.getFullYear(), currentDate.getMonth());
    const selectedDateStr = formatLocalDate(currentDate);
    const selectedEntries = groupedTimesheets.filter(ts => {
        const tsDate = ts.date ? ts.date.split('T')[0].split(' ')[0] : '';
        return tsDate === selectedDateStr;
    });

    const displayEntries = useMemo(() => {
        if (viewMode === 'day') return selectedEntries;
        
        let others = [];
        if (viewMode === 'week') {
            const startOfWeek = formatLocalDate(weekDays[0]);
            const endOfWeek = formatLocalDate(weekDays[6]);
            others = groupedTimesheets.filter(ts => {
                const tsDate = ts.date ? ts.date.split('T')[0].split(' ')[0] : '';
                return tsDate !== selectedDateStr && tsDate >= startOfWeek && tsDate <= endOfWeek;
            });
        } else if (viewMode === 'month') {
            const startOfMonth = formatLocalDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), 1));
            const endOfMonth = formatLocalDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0));
            others = groupedTimesheets.filter(ts => {
                const tsDate = ts.date ? ts.date.split('T')[0].split(' ')[0] : '';
                return tsDate !== selectedDateStr && tsDate >= startOfMonth && tsDate <= endOfMonth;
            });
        }
        
        // Ordre chronologique pour le reste (du plus ancien au plus récent)
        others.sort((a, b) => a.date.localeCompare(b.date));

        return [...selectedEntries, ...others];
    }, [viewMode, selectedEntries, groupedTimesheets, weekDays, currentDate, selectedDateStr]);

    // EDIT MODAL LOGIC
    const handleEditClick = (entry, fallbackDate) => {
        if (entry && entry.status === 'approved') {
            toast.info("Une fiche validée ne peut plus être modifiée directement. Utilisez le bouton réclamation.");
            return;
        }

        const dateStr = entry?.date?.split('T')[0] || formatLocalDate(fallbackDate);
        const eventOnDate = todayPlanning.find(p => {
            const evDate = p.start?.substring(0, 10) || formatLocalDate(p.fullDate);
            return evDate === dateStr;
        });

        if (entry) {
            const projId = entry.chantier || entry.projects?.[0]?.id || '';
            const currentProjects = entry.projects || [];
            const targetId = entry.originalEntries?.[0]?.id || entry.id;

            setEditingEntry({
                ...entry,
                id: targetId,
                date: dateStr,
                chantier: projId,
                projects: currentProjects,
                morning_start: entry.morning_start || "08:00",
                morning_end: entry.morning_end || "12:00",
                afternoon_start: entry.afternoon_start || "13:00",
                afternoon_end: entry.afternoon_end || "17:00",
                hours: parseFloat(entry.hours || 8.0)
            });
        } else if (fallbackDate) {
            setEditingEntry({
                id: `new-${dateStr}`,
                date: dateStr,
                isSuggested: true,
                morning_start: "08:00",
                morning_end: "12:00",
                afternoon_start: "13:00",
                afternoon_end: "17:00",
                hours: 8.0,
                chantier: eventOnDate?.expand?.project?.id || '',
                projects: eventOnDate?.expand?.project ? [eventOnDate.expand.project] : [],
                description: ""
            });
        }
        setIsEditModalOpen(true);
    };

    const handleTimeChange = (field, value) => {
        const newEntry = { ...editingEntry, [field]: value };
        const calcHours = (start, end) => {
            if (!start || !end || start === "--:--" || end === "--:--") return 0;
            const [hS, mS] = start.split(':').map(Number);
            const [hE, mE] = end.split(':').map(Number);
            let diff = (hE + mE / 60) - (hS + mS / 60);
            return diff > 0 ? diff : 0;
        };
        const totalMat = calcHours(newEntry.morning_start, newEntry.morning_end);
        const totalAM = calcHours(newEntry.afternoon_start, newEntry.afternoon_end);
        newEntry.hours = parseFloat((totalMat + totalAM).toFixed(2));
        setEditingEntry(newEntry);
    };

    const handleSaveEdit = async () => {
        try {
            const data = {
                morning_start: editingEntry.morning_start,
                morning_end: editingEntry.morning_end,
                afternoon_start: editingEntry.afternoon_start,
                afternoon_end: editingEntry.afternoon_end,
                hours: parseFloat(editingEntry.hours),
                description: editingEntry.description,
                worker: user.id,
                date: editingEntry.date,
                chantier: editingEntry.projects?.[0]?.id || editingEntry.chantier
            };

            if (editingEntry.isSuggested) {
                await pb.collection('timesheets').create(data);
            } else {
                const entriesToUpdate = editingEntry.originalEntries || [{ id: editingEntry.id, status: editingEntry.status }];
                const mainId = entriesToUpdate[0].id;
                if (entriesToUpdate.some(e => e.status === 'rejected' || e.status === 'litige')) {
                    data.status = 'pending_manager';
                }
                await pb.collection('timesheets').update(mainId, data);

                if (editingEntry.originalEntries && editingEntry.originalEntries.length > 1) {
                    for (let i = 1; i < editingEntry.originalEntries.length; i++) {
                        try { await pb.collection('timesheets').delete(editingEntry.originalEntries[i].id); } catch (e) {}
                    }
                }
            }
            toast.success("Heures enregistrées !");
            setIsEditModalOpen(false);
            fetchStaffData();
        } catch (err) {
            toast.error("Erreur lors de l'enregistrement.");
        }
    };

    const handleDeleteEntry = async (id) => {
        if (!window.confirm("Voulez-vous supprimer cette fiche ?")) return;
        try {
            await pb.collection('timesheets').delete(id);
            toast.success("Fiche supprimée.");
            fetchStaffData();
        } catch (err) {
            toast.error("Erreur de suppression.");
        }
    };

    const handleOpenClaim = (entry) => {
        setClaimEntry(entry.originalEntries?.[0] || entry);
        setIsClaimModalOpen(true);
    };

    const handleSendClaim = async () => {
        if (!claimReason.trim()) { toast.error("Veuillez expliquer le motif."); return; }
        try {
            const newDesc = `[RÉCLAMATION] : ${claimReason}\n---\n${claimEntry.description || ''}`;
            await pb.collection('timesheets').update(claimEntry.id, { status: 'litige', description: newDesc });
            toast.success("Réclamation transmise !");
            setIsClaimModalOpen(false);
            setClaimReason('');
            fetchStaffData();
        } catch (err) { toast.error("Erreur d'envoi."); }
    };

    // COLOR LOGIC
    const getStatusColor = (status) => {
        switch(status) {
            case 'approved': return '#10b981'; // Green
            case 'pending_manager':
            case 'pending_rh': return '#f59e0b'; // Orange
            case 'rejected':
            case 'litige': return '#ef4444'; // Red
            case 'suggested': return '#3b82f6'; // Blue
            case 'missing': return '#ef4444'; // Red
            case 'future': return '#d1d5db'; // Gray
            default: return '#d1d5db';
        }
    };

    const getStatusLabel = (status) => {
        switch(status) {
            case 'approved': return 'VALIDÉ';
            case 'pending_manager':
            case 'pending_rh': return 'EN ATTENTE';
            case 'rejected': return 'REFUSÉ';
            case 'litige': return 'LITIGE';
            case 'suggested': return 'À VALIDER';
            case 'missing': return 'NON SAISI';
            default: return 'INCONNU';
        }
    };

    return (
        <div className="worker-hours-page scale-in">
            {/* Header & Stats */}
            <div className="w-page-header" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                <div style={{ marginBottom: '20px' }}>
                    <h2 className="w-page-title">Mes Heures</h2>
                    <p className="w-page-subtitle">Suivi du pointage et validaton</p>
                </div>
                
                {/* Stats Grid */}
                <div className="stats-grid">
                    <div className="stat-card" style={{ borderLeftColor: '#3b82f6' }}>
                        <span className="stat-label">Cette Semaine</span>
                        <div className="stat-value">{bonuses?.weeklyHours || 0}h <span className="stat-sub">/ 39h</span></div>
                        <div className="stat-bar"><div className="stat-fill" style={{ width: `${Math.min(100, ((bonuses?.weeklyHours || 0) / 39) * 100)}%`, background: '#3b82f6' }}></div></div>
                    </div>
                    <div className="stat-card" style={{ borderLeftColor: '#8b5cf6' }}>
                        <span className="stat-label">Cumul Mois</span>
                        <div className="stat-value">{bonuses?.monthlyHours || 0}h</div>
                        <span className="stat-sub">Heures cumulées</span>
                    </div>
                    <div className="stat-card" style={{ borderLeftColor: '#10b981' }}>
                        <span className="stat-label">Heures Sup'</span>
                        <div className="stat-value" style={{ color: '#10b981' }}>+{bonuses?.overtime || 0}h</div>
                        <span className="stat-sub">Sur le mois</span>
                    </div>
                    <div className="stat-card" style={{ borderLeftColor: '#fbbf24' }}>
                        <span className="stat-label">Paniers Repas</span>
                        <div className="stat-value"><FaUtensils style={{ fontSize: '14px', color: '#fbbf24', marginRight: '4px' }}/> {bonuses?.lunchVouchers || 0}</div>
                        <span className="stat-sub">Nombre de jours</span>
                    </div>
                </div>
            </div>

            {/* View Switcher */}
            <div style={{ padding: '0 20px 15px' }}>
                <div className="w-view-switch">
                    <button className={viewMode === 'day' ? 'active' : ''} onClick={() => setViewMode('day')}>Jour</button>
                    <button className={viewMode === 'week' ? 'active' : ''} onClick={() => setViewMode('week')}>Sem.</button>
                    <button className={viewMode === 'month' ? 'active' : ''} onClick={() => setViewMode('month')}>Mois</button>
                    <button className={viewMode === 'list' ? 'active' : ''} onClick={() => setViewMode('list')}>Liste</button>
                    <button className={viewMode === 'raw' ? 'active' : ''} onClick={() => setViewMode('raw')}>Brute</button>
                </div>
            </div>

            {/* Weekly Navigation */}
            {(viewMode === 'week' || viewMode === 'day') && (
                <div className="week-nav-container">
                    <div className="week-nav-header">
                        <button className="week-nav-btn" onClick={() => {
                            const d = new Date(currentDate); d.setDate(d.getDate() - 7); setCurrentDate(d);
                        }}><FaChevronLeft /></button>
                        <span className="week-range-label">
                            {weekDays[0].toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} - {weekDays[6].toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                        </span>
                        <button className="week-nav-btn" onClick={() => {
                            const d = new Date(currentDate); d.setDate(d.getDate() + 7); setCurrentDate(d);
                        }}><FaChevronRight /></button>
                    </div>
                    <div className="week-days-row">
                        {weekDays.map((d, i) => {
                            const isSelected = d.toDateString() === currentDate.toDateString();
                            const isToday = d.toDateString() === new Date().toDateString();
                            const status = getStatusForDate(d);
                            const dotColor = getStatusColor(status);

                            return (
                                <div 
                                    key={i} 
                                    className={`day-cell ${isSelected ? 'selected' : ''}`}
                                    onClick={() => setCurrentDate(d)}
                                >
                                    <span className="w-day-name">{d.toLocaleDateString('fr-FR', { weekday: 'short' }).charAt(0)}</span>
                                    <span className={`w-day-num ${isToday && !isSelected ? 'is-today' : ''}`}>{d.getDate()}</span>
                                    {status !== 'future' && (
                                        <div className="status-dot" style={{ background: dotColor }}></div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Monthly Navigation */}
            {viewMode === 'month' && (
                <div className="mini-cal">
                    <div className="cal-header">
                        <button onClick={() => { const d = new Date(currentDate); d.setMonth(d.getMonth() - 1); setCurrentDate(d); }}><FaChevronLeft /></button>
                        <span className="month-label">{currentDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}</span>
                        <button onClick={() => { const d = new Date(currentDate); d.setMonth(d.getMonth() + 1); setCurrentDate(d); }}><FaChevronRight /></button>
                    </div>
                    <div className="cal-grid">
                        {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map(d => <div key={d} className="cal-day-name">{d}</div>)}
                        {monthDays.map((d, i) => {
                            if (!d) return <div key={i} className="cal-cell empty"></div>;
                            const isSelected = d.toDateString() === currentDate.toDateString();
                            const status = getStatusForDate(d);
                            const dotColor = getStatusColor(status);
                            
                            return (
                                <div key={i} className={`cal-cell ${isSelected ? 'selected' : ''}`} onClick={() => setCurrentDate(d)}>
                                    <span className="cal-date">{d.getDate()}</span>
                                    {status !== 'future' && <div className="status-dot" style={{ background: dotColor }}></div>}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Content List */}
            <div className="w-cards-list">
                {viewMode !== 'list' && viewMode !== 'raw' && (
                    <div className="w-date-header">
                        {currentDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                    </div>
                )}

                {/* LIVE COCKPIT (Always at the top if looking at today or in list mode) */}
                {(selectedDateStr === formatLocalDate(new Date()) || viewMode === 'list') && (activeWorkSession || todaySummary) && (
                    <div className="w-card live-card">
                        <div className="live-header">
                            <span className="live-pulse"></span>
                            <span className="live-title">EN DIRECT</span>
                        </div>
                        {activeWorkSession ? (
                            <>
                                <div className="live-chantier">{activeWorkSession.expand?.chantier?.name || activeWorkSession.type || 'Chantier Général'}</div>
                                <div className="live-timer">{formatElapsedTime(elapsedTime)}</div>
                                <div className="live-actions">
                                    <button className="live-btn stop" onClick={handleStopSession}><FaStopwatch /> Terminer</button>
                                </div>
                            </>
                        ) : (
                            <div className="live-chantier" style={{ textAlign: 'center', margin: '20px 0' }}>
                                Journée terminée ({todaySummary?.hours}h)
                            </div>
                        )}
                    </div>
                )}

                {/* ENTRIES FOR THE DAY OR FULL LIST */}
                {(viewMode === 'list' || viewMode === 'raw') ? (
                    (viewMode === 'raw' ? rawTimesheets : groupedTimesheets).map(entry => {
                        const statusColor = getStatusColor(entry.status);
                        
                        return (
                            <div key={entry.id} className="w-card premium list-item" style={{ borderLeft: `4px solid ${statusColor}`, marginBottom: '10px' }}>
                                <div className="list-item-top">
                                    <span style={{ fontSize: '13px', fontWeight: 800, textTransform: 'capitalize', color: '#64748b' }}>
                                        {new Date(entry.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                                    </span>
                                    <div className="w-badge" style={{ background: `${statusColor}22`, color: statusColor, fontSize: '9px' }}>
                                        {getStatusLabel(entry.status)}
                                    </div>
                                </div>
                                <div className="list-item-mid">
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: '14px', fontWeight: 900, marginBottom: '4px' }}>
                                            {entry.projects?.map(p => p.name).join(', ') || 'Général'}
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#94a3b8', display: 'flex', gap: '8px' }}>
                                            <span>{entry.morning_start || '--'} - {entry.morning_end || '--'}</span>
                                            <span style={{ opacity: 0.3 }}>|</span>
                                            <span>{entry.afternoon_start || '--'} - {entry.afternoon_end || '--'}</span>
                                        </div>
                                    </div>
                                    <div style={{ fontSize: '18px', fontWeight: 900, color: statusColor, marginLeft: '10px' }}>
                                        {entry.hours}h
                                    </div>
                                </div>
                                <div className="list-item-bot">
                                    {entry.status === 'approved' ? (
                                        <button className="w-btn-outline compact" onClick={() => handleOpenClaim(entry)}><FaCommentDots /> Réclamation</button>
                                    ) : (
                                        <div style={{ display: 'flex', gap: '6px' }}>
                                            <button className="w-btn-outline compact" onClick={() => handleEditClick(entry)}><FaEdit /> Modifier</button>
                                            {!entry.isSuggested && (
                                                <button className="w-btn-outline compact danger" onClick={() => handleDeleteEntry(entry.id)}><FaTrash /></button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <>
                        {viewMode !== 'list' && viewMode !== 'raw' && selectedEntries.length === 0 && (
                            selectedDateStr <= formatLocalDate(new Date()) ? (
                                <div className="w-empty-state" onClick={() => handleEditClick(null, currentDate)} style={{ cursor: 'pointer', marginBottom: '15px' }}>
                                    <div className="empty-icon"><FaExclamationTriangle style={{ color: '#f59e0b' }} /></div>
                                    <h3>Aucune heure déclarée</h3>
                                    <p>Cliquez ici pour ajouter vos heures manuellement pour ce jour.</p>
                                </div>
                            ) : (
                                <div className="w-empty-state" style={{ marginBottom: '15px' }}>
                                    <div className="empty-icon"><FaCalendarAlt /></div>
                                    <h3>Date future</h3>
                                </div>
                            )
                        )}
                        
                        {displayEntries.length > 0 && displayEntries.map(entry => {
                            const statusColor = getStatusColor(entry.status);
                            const tsDate = entry.date ? entry.date.split('T')[0].split(' ')[0] : '';
                            const isSelectedDay = tsDate === selectedDateStr;
                            
                            return (
                                <div key={entry.id} className="w-card premium" style={{ borderLeft: `4px solid ${statusColor}`, opacity: isSelectedDay ? 1 : 0.85 }}>
                                    <div className="w-card-header">
                                        <div>
                                            {viewMode !== 'day' && (
                                                <div style={{ fontSize: '10px', color: isSelectedDay ? statusColor : '#64748b', textTransform: 'uppercase', fontWeight: 800, marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    {isSelectedDay ? <><FaCalendarDay /> JOUR SÉLECTIONNÉ</> : new Date(entry.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                                                </div>
                                            )}
                                            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 900 }}>
                                                {entry.projects?.map(p => p.name).join(', ') || 'Général'}
                                            </h3>
                                        </div>
                                        <div className="w-badge" style={{ background: `${statusColor}22`, color: statusColor }}>
                                            {getStatusLabel(entry.status)}
                                        </div>
                                    </div>
                                    <div className="w-card-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                            <div className="w-label-row">
                                                <FaClock /> <span>{entry.morning_start || '--'} - {entry.morning_end || '--'} (Matin)</span>
                                            </div>
                                            <div className="w-label-row">
                                                <FaClock /> <span>{entry.afternoon_start || '--'} - {entry.afternoon_end || '--'} (A-M)</span>
                                            </div>
                                        </div>
                                        <div style={{ fontSize: '1.5rem', fontWeight: 900, color: statusColor }}>
                                            {entry.hours}h
                                        </div>
                                    </div>
                                    
                                    <div className="w-card-footer" style={{ justifyContent: 'flex-end', gap: '10px' }}>
                                        {entry.status === 'approved' ? (
                                            <button className="w-btn-outline" onClick={() => handleOpenClaim(entry)}>
                                                <FaCommentDots /> Réclamation
                                            </button>
                                        ) : (
                                            <>
                                                <button className="w-btn-outline" onClick={() => handleEditClick(entry)}>
                                                    <FaEdit /> Modifier
                                                </button>
                                                {!entry.isSuggested && (
                                                    <button className="w-btn-outline danger" onClick={() => handleDeleteEntry(entry.id)}>
                                                        <FaTrash />
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        {displayEntries.length === 0 && viewMode !== 'list' && viewMode !== 'raw' && selectedEntries.length > 0 && (
                           null /* already handled by map if it wasn't empty */
                        )}
                        {displayEntries.length === 0 && (viewMode === 'list' || viewMode === 'raw') && (
                            <div className="w-empty-state">
                                <h3>Aucune donnée</h3>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Export Bottom Buttons */}
            <div style={{ padding: '20px', display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button className="w-btn-full" onClick={exportWeeklyToCSV} style={{ flex: 1, background: '#334155' }}><FaFileCsv /> CSV</button>
                <button className="w-btn-full" onClick={exportWeeklyToPDF} style={{ flex: 1, background: '#e11d48' }}><FaFileDownload /> PDF</button>
            </div>

            {/* MODALS */}
            {isEditModalOpen && editingEntry && (
                <div className="modal-overlay" onClick={() => setIsEditModalOpen(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <h3>{editingEntry.isSuggested ? "Ajouter Pointage" : "Modifier Fiche"}</h3>
                        <div className="form-group">
                            <label>Date</label>
                            <input type="date" value={editingEntry.date} onChange={e => setEditingEntry({...editingEntry, date: e.target.value})} />
                        </div>
                        <div className="form-group">
                            <label>Projet</label>
                            <select value={editingEntry.chantier} onChange={e => setEditingEntry({...editingEntry, chantier: e.target.value})}>
                                <option value="">Général / Atelier</option>
                                {staffProjects?.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                            <div className="form-group"><label>Matin Début</label><input type="time" value={editingEntry.morning_start} onChange={e => handleTimeChange('morning_start', e.target.value)} /></div>
                            <div className="form-group"><label>Matin Fin</label><input type="time" value={editingEntry.morning_end} onChange={e => handleTimeChange('morning_end', e.target.value)} /></div>
                            <div className="form-group"><label>A-M Début</label><input type="time" value={editingEntry.afternoon_start} onChange={e => handleTimeChange('afternoon_start', e.target.value)} /></div>
                            <div className="form-group"><label>A-M Fin</label><input type="time" value={editingEntry.afternoon_end} onChange={e => handleTimeChange('afternoon_end', e.target.value)} /></div>
                        </div>
                        <div className="form-group">
                            <label>Total Heures</label>
                            <input type="number" step="0.5" value={editingEntry.hours} onChange={e => setEditingEntry({...editingEntry, hours: e.target.value})} />
                        </div>
                        <div className="modal-actions">
                            <button className="btn-cancel" onClick={() => setIsEditModalOpen(false)}>Annuler</button>
                            <button className="btn-save" onClick={handleSaveEdit}>Enregistrer</button>
                        </div>
                    </div>
                </div>
            )}

            {isClaimModalOpen && (
                <div className="modal-overlay" onClick={() => setIsClaimModalOpen(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <h3>Réclamation</h3>
                        <p style={{ fontSize: '0.85rem', opacity: 0.8, marginBottom: '15px' }}>Expliquez le motif de la correction pour la fiche du {new Date(claimEntry?.date).toLocaleDateString()}.</p>
                        <textarea 
                            value={claimReason} 
                            onChange={e => setClaimReason(e.target.value)} 
                            placeholder="Ex: J'ai oublié 1h le matin..."
                            style={{ width: '100%', padding: '10px', height: '100px', borderRadius: '8px', border: '1px solid #ccc' }}
                        />
                        <div className="modal-actions" style={{ marginTop: '15px' }}>
                            <button className="btn-cancel" onClick={() => setIsClaimModalOpen(false)}>Annuler</button>
                            <button className="btn-save" onClick={handleSendClaim}>Envoyer</button>
                        </div>
                    </div>
                </div>
            )}

            {/* CSS INJECTED */}
            <style>{`
                .worker-hours-page { min-height: 100vh; background: #f8fafc; margin-top: -20px; padding-bottom: 80px; }
                .w-page-header { padding: 30px 20px 15px; display: flex; justify-content: space-between; align-items: flex-end; }
                .w-page-title { font-size: 22px; font-weight: 900; color: #0f172a; margin: 0; }
                .w-page-subtitle { font-size: 13px; color: #94a3b8; margin: 4px 0 0; }

                .w-view-switch { display: flex; background: #e2e8f0; padding: 4px; border-radius: 12px; }
                .w-view-switch button { flex: 1; padding: 8px; border: none; background: transparent; border-radius: 8px; font-weight: 700; color: #64748b; font-size: 13px; cursor: pointer; transition: 0.2s; }
                .w-view-switch button.active { background: #fff; color: var(--color-primary); box-shadow: 0 2px 5px rgba(0,0,0,0.05); }

                .stats-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
                .stat-card { background: #fff; border-radius: 12px; padding: 12px; border: 1px solid #f1f5f9; border-left: 4px solid #3b82f6; display: flex; flex-direction: column; }
                .stat-label { font-size: 10px; font-weight: 800; text-transform: uppercase; color: #64748b; margin-bottom: 4px; }
                .stat-value { font-size: 18px; font-weight: 900; color: #0f172a; display: flex; align-items: center; }
                .stat-sub { font-size: 10px; opacity: 0.5; margin-left: 4px; font-weight: 700; }
                .stat-bar { height: 4px; background: #e2e8f0; border-radius: 2px; margin-top: 6px; overflow: hidden; }
                .stat-fill { height: 100%; border-radius: 2px; }

                .week-nav-container { background: #fff; border-radius: 16px; margin: 0 20px 20px; padding: 15px; border: 1px solid #f1f5f9; }
                .week-nav-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px; }
                .week-range-label { font-weight: 800; font-size: 14px; color: #0f172a; text-transform: capitalize; }
                .week-nav-btn { width: 32px; height: 32px; border-radius: 8px; border: none; background: #f8fafc; color: #64748b; display: flex; align-items: center; justify-content: center; cursor: pointer; }

                .week-days-row { display: flex; justify-content: space-between; gap: 5px; }
                .day-cell { flex: 1; display: flex; flex-direction: column; align-items: center; padding: 10px 5px; border-radius: 12px; cursor: pointer; color: #475569; position: relative; }
                .day-cell.selected { background: var(--color-primary); color: #fff; box-shadow: 0 4px 10px rgba(0,0,0,0.1); }
                .w-day-name { font-size: 11px; font-weight: 700; text-transform: uppercase; margin-bottom: 4px; }
                .w-day-num { font-size: 16px; font-weight: 900; }
                .w-day-num.is-today { color: var(--color-primary); }
                .status-dot { width: 6px; height: 6px; border-radius: 50%; position: absolute; bottom: 4px; }

                .mini-cal { background: #fff; border-radius: 16px; margin: 0 20px 20px; padding: 15px; border: 1px solid #f1f5f9; }
                .cal-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px; }
                .cal-header button { width: 32px; height: 32px; border-radius: 8px; border: none; background: #f8fafc; color: #64748b; cursor: pointer; display: flex; align-items: center; justify-content: center; }
                .month-label { font-weight: 800; font-size: 15px; text-transform: capitalize; color: #0f172a; }
                .cal-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 5px; text-align: center; }
                .cal-day-name { font-size: 11px; font-weight: 800; color: #94a3b8; margin-bottom: 10px; }
                .cal-cell { aspect-ratio: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; border-radius: 10px; font-size: 14px; font-weight: 700; cursor: pointer; color: #334155; position: relative; }
                .cal-cell.empty { visibility: hidden; }
                .cal-cell.selected { background: var(--color-primary); color: #fff; }
                
                .w-cards-list { padding: 0 20px; }
                .w-date-header { font-size: 14px; font-weight: 800; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 15px; }

                .w-card { background: #fff; border-radius: 16px; padding: 16px; margin-bottom: 15px; border: 1px solid #f1f5f9; }
                .w-card-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 15px; }
                .w-badge { padding: 4px 10px; border-radius: 6px; font-size: 10px; font-weight: 800; text-transform: uppercase; }
                .w-label-row { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #64748b; font-weight: 600; }
                
                .w-card-footer { margin-top: 15px; padding-top: 15px; border-top: 1px solid #f8fafc; display: flex; }
                .w-btn-outline { background: transparent; border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px 12px; font-size: 12px; font-weight: 700; display: flex; align-items: center; gap: 6px; cursor: pointer; color: #475569; }
                .w-btn-outline.danger { color: #ef4444; border-color: #fee2e2; }
                .w-btn-outline.compact { padding: 6px 10px; font-size: 11px; }

                .list-item { padding: 12px; }
                .list-item-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
                .list-item-mid { display: flex; justify-content: space-between; align-items: center; }
                .list-item-bot { display: flex; justify-content: flex-end; margin-top: 10px; padding-top: 10px; border-top: 1px solid #f8fafc; }

                .live-card { background: #0f172a; color: #fff; border: none; box-shadow: 0 10px 25px rgba(15, 23, 42, 0.2); }
                .live-header { display: flex; align-items: center; gap: 8px; font-size: 12px; font-weight: 800; color: #ef4444; margin-bottom: 10px; }
                .live-pulse { width: 8px; height: 8px; background: #ef4444; border-radius: 50%; animation: pulse 1.5s infinite; }
                .live-chantier { font-size: 18px; font-weight: 900; margin-bottom: 5px; }
                .live-timer { font-size: 32px; font-weight: 900; letter-spacing: 2px; font-variant-numeric: tabular-nums; margin-bottom: 15px; color: #38bdf8; }
                .live-actions { display: flex; gap: 10px; }
                .live-btn { flex: 1; padding: 12px; border-radius: 10px; font-weight: 800; border: none; display: flex; align-items: center; justify-content: center; gap: 8px; cursor: pointer; }
                .live-btn.stop { background: #ef4444; color: #fff; }

                .w-empty-state { text-align: center; padding: 40px 20px; background: #fff; border-radius: 16px; border: 1px dashed #cbd5e1; }
                .empty-icon { width: 48px; height: 48px; border-radius: 16px; background: #f8fafc; display: flex; align-items: center; justify-content: center; margin: 0 auto 15px; font-size: 20px; color: #94a3b8; }
                .w-empty-state h3 { margin: 0 0 5px; font-size: 16px; color: #0f172a; }
                .w-empty-state p { margin: 0; font-size: 13px; color: #64748b; }

                .w-btn-full { padding: 14px; border-radius: 12px; border: none; font-weight: 800; color: #fff; display: flex; justify-content: center; align-items: center; gap: 8px; cursor: pointer; }

                @keyframes pulse { 0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4); } 70% { box-shadow: 0 0 0 10px rgba(239, 68, 68, 0); } 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); } }

                .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 20px; }
                .modal-content { background: #fff; border-radius: 16px; padding: 20px; width: 100%; max-width: 400px; }
                .modal-content h3 { margin: 0 0 20px; font-size: 18px; }
                .form-group { margin-bottom: 15px; }
                .form-group label { display: block; font-size: 12px; font-weight: 800; color: #64748b; margin-bottom: 5px; }
                .form-group input, .form-group select { width: 100%; padding: 10px; border-radius: 8px; border: 1px solid #e2e8f0; font-family: inherit; }
                .modal-actions { display: flex; gap: 10px; margin-top: 20px; }
                .modal-actions button { flex: 1; padding: 12px; border-radius: 8px; font-weight: 800; border: none; cursor: pointer; }
                .btn-cancel { background: #f1f5f9; color: #475569; }
                .btn-save { background: var(--color-primary); color: #fff; }

                /* DARK MODE */
                [data-theme="dark"] .worker-hours-page { background: var(--color-background, #121212); }
                [data-theme="dark"] .w-page-title, [data-theme="dark"] .week-range-label, [data-theme="dark"] .month-label, [data-theme="dark"] .w-day-num { color: #ffffff; }
                [data-theme="dark"] .w-page-subtitle { color: #a1a1aa; }
                [data-theme="dark"] .w-view-switch { background: #27272a; }
                [data-theme="dark"] .w-view-switch button.active { background: #18181b; }
                [data-theme="dark"] .stat-card { background: #1e1e1e; border-color: rgba(255,255,255,0.08); }
                [data-theme="dark"] .stat-value { color: #fff; }
                [data-theme="dark"] .stat-bar { background: #3f3f46; }
                [data-theme="dark"] .week-nav-container, [data-theme="dark"] .mini-cal, [data-theme="dark"] .w-card { background: #1e1e1e; border-color: rgba(255,255,255,0.08); }
                [data-theme="dark"] .week-nav-btn, [data-theme="dark"] .cal-header button, [data-theme="dark"] .empty-icon { background: rgba(255,255,255,0.05); color: #d4d4d8; }
                [data-theme="dark"] .day-cell { color: #a1a1aa; }
                [data-theme="dark"] .w-card-footer, [data-theme="dark"] .list-item-bot { border-top-color: rgba(255,255,255,0.05); }
                [data-theme="dark"] .w-btn-outline { border-color: rgba(255,255,255,0.1); color: #e4e4e7; }
                [data-theme="dark"] .w-empty-state { background: #1e1e1e; border-color: rgba(255,255,255,0.1); }
                [data-theme="dark"] .w-empty-state h3 { color: #e4e4e7; }
                [data-theme="dark"] .live-card { background: #0f172a; }
                [data-theme="dark"] .modal-content { background: #1e1e1e; color: #fff; }
                [data-theme="dark"] .form-group input, [data-theme="dark"] .form-group select, [data-theme="dark"] .modal-content textarea { background: #27272a; border-color: #3f3f46; color: #fff; }
            `}</style>
        </div>
    );
};

export default TabMyHours;
